import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const DISMISSED_KEY = "spectraloom_pwa_install_dismissed";

/**
 * PWA install button.
 *
 * Behaviour:
 *   - Hidden by default.
 *   - Listens for the browser's `beforeinstallprompt` event (Chromium /
 *     Edge / Samsung Internet). When fired, we stash the event and reveal
 *     the button. On click we call `prompt()` and hide the button once
 *     the user makes a choice (accepted OR dismissed the native dialog).
 *   - When the app has already been installed (`appinstalled` event or
 *     `display-mode: standalone`) we stay hidden.
 *   - When the user closes the button with the × we remember it in
 *     localStorage so it doesn't nag them again on this device.
 *   - iOS Safari does NOT support `beforeinstallprompt` so this component
 *     stays hidden there (per product spec).
 *
 * Position:
 *   - Mobile (default): fixed pill above the legal footer.
 *   - Desktop (sm+):    fixed top-right, below the header banner strip.
 */
export default function PWAInstallButton() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed / running as PWA · never show.
    if (typeof window === "undefined") return;
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // User previously closed the pill · respect the choice.
    try {
      if (localStorage.getItem(DISMISSED_KEY) === "1") return;
    } catch {
      /* localStorage disabled · proceed anyway */
    }

    const onBeforeInstall = (e) => {
      e.preventDefault(); // prevent Chrome's automatic mini-infobar
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setVisible(false);
      try {
        localStorage.setItem(DISMISSED_KEY, "1");
      } catch {
        /* ignore */
      }
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch {
      /* user cancelled / prompt already consumed */
    } finally {
      // A deferred prompt can only be used once · drop it either way so
      // the button doesn't linger after the native dialog is closed.
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-button"
      className={[
        // Mobile: pinned to bottom-center, floating above the legal footer.
        "fixed left-1/2 -translate-x-1/2 bottom-4 z-[90]",
        // Desktop: reset the mobile pinning and dock in the top-right.
        "sm:left-auto sm:right-4 sm:top-20 sm:bottom-auto sm:translate-x-0",
        "flex items-center gap-1",
      ].join(" ")}
      role="region"
      aria-label={t("pwa_install.aria")}
    >
      <button
        type="button"
        onClick={install}
        data-testid="pwa-install-cta"
        className={[
          "group inline-flex items-center gap-2 pl-4 pr-4 py-2.5 rounded-full",
          "bg-gradient-to-r from-blue-500 to-violet-500 text-white text-sm font-semibold",
          "shadow-lg shadow-blue-500/30 border border-white/10 backdrop-blur-sm",
          "hover:from-blue-400 hover:to-violet-400 hover:shadow-blue-500/40",
          "active:scale-[0.98] transition-all",
        ].join(" ")}
      >
        <Download size={16} className="shrink-0" />
        <span>{t("pwa_install.cta")}</span>
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("pwa_install.dismiss")}
        data-testid="pwa-install-dismiss"
        className={[
          "shrink-0 h-9 w-9 rounded-full flex items-center justify-center",
          "bg-white/10 text-white/80 border border-white/10 backdrop-blur-sm",
          "hover:bg-white/20 hover:text-white transition-colors",
        ].join(" ")}
      >
        <X size={14} />
      </button>
    </div>
  );
}
